/**
 * Parsing and coercion of dynamic property values.
 */
const CSS_DIMENSION_TOKEN = /^([+-]?(?:\d*\.\d+|\d+)(?:e[+-]?\d+)?)([%a-z]*)$/i;

/**
 * Strict string-to-number conversion, preferred over parseFloat/parseInt
 * because those read a leading numeric *prefix* and silently discard the rest -
 * parseFloat('10px') is 10, which hides malformed data wherever a bare number
 * was expected.
 *
 * Number() is stricter about that but looser at the other end: Number('') and
 * Number('   ') are both 0, so the empty check is not optional. Returns
 * undefined rather than NaN so callers have to decide what absence means.
 */
export function toFiniteNumber(text: string): number | undefined {
    if (text.trim() === '') return undefined;
    const converted = Number(text);
    return isFinite(converted) ? converted : undefined;
}

/**
 * The coercion shared by every numeric property type's `transform`. Kept here
 * rather than on a base class because Number and the UnitNumber family are
 * deliberately unrelated (different CSS value types), but agree completely on
 * what counts as a number.
 *
 * Returns 0 rather than undefined for unreadable input. Undefined is meaningful
 * in exactly one place - setState's create branch warns and then omits the
 * property entirely (see 'Error creating type' in Group.ts) - while add(), set()
 * and setState's existing-property branch all store it silently. Losing a
 * property because one number was unreadable is worse than coercing it, and
 * getString() renders undefined the same as 0 anyway, so the two are
 * indistinguishable on screen. This way the stored value agrees with what is
 * displayed, and the warning is the part that carries the information.
 *
 * The `originProperty` parameter is structural on purpose, so this module
 * stays free of imports. It is only used for diagnostic messages.
 */
export function transformToNumber(
    value: unknown,
    originProperty: { type: string; name: string }
): number {
    if (typeof value === 'number' && isFinite(value)) return value;

    if (typeof value === 'string') {
        const converted = toFiniteNumber(value);
        if (converted !== undefined) return converted;
    }

    console.warn(
        `[transformNumber]: Setting ${originProperty.name}:${originProperty.type}: Using 0`,
        value
    );
    return 0;
}

export function parseUnitValue(text: string) {
    // TODO: adhublib version has tests
    //  (this is now the adhublib version, the real todo is allow
    //   inclusion of adhublib)

    const match = CSS_DIMENSION_TOKEN.exec(text.trim());
    if (!match) return undefined;

    const quantity = +match[1];
    const unit = match[2] || '';

    // only a quantity of 0 allows the unit to be omitted
    if (!unit && quantity !== 0) return null;

    // TODO: property names ought to be { value, unit } for parity with
    //  DOM's CSSUnitValue class. Applies to adhublib version as well
    return { value: quantity, type: unit };
}

/**
 * Parses a `WxH` dimensions string. Case-insensitive on the separator, so
 * `300X250` is accepted as readily as `300x250` - CSV data gets hand-edited, and
 * the counterpart formatter only ever emits lowercase.
 *
 * Splitting on `x` also rejects a unit-suffixed pair like `300px x 250px`, which
 * lands as four parts, so each half is expected to be a bare number.
 *
 * toFiniteNumber rather than a bare `+`: `+''` is 0 and `isFinite(0)` is true,
 * so `"x250"` would otherwise parse as `[0, 250]` and import silently, with the
 * complaint surfacing much later and less clearly as "missing valid dimensions"
 * once getTuple rejects the zero.
 */
export function parseDimensions(
    dimensionsString: string | undefined
): [number, number] | undefined {
    if (dimensionsString) {
        const parts = dimensionsString.split(/x/i);
        if (parts.length === 2) {
            const width = toFiniteNumber(parts[0]);
            const height = toFiniteNumber(parts[1]);
            if (width !== undefined && height !== undefined) {
                return [width, height];
            }
        }
    }
    return undefined;
}
